/**
 * LW24 Funnel Preview Capture - Reusable Capture & Upload Logic
 *
 * Erstellt 30.04.2026. Wiederverwendbar fuer ALLE personalisierbare Lampen-Funnels
 * (Ortstafel-Eingang/Ausgang, HU-Plakette, U-Bahn, KFZ-Kennzeichen, Logo, kuenftige).
 *
 * Architektur:
 *   1. html2canvas wird lazy via CDN geladen (1x pro Page-Load)
 *   2. Captured wird das Preview-Element (Schild/Plakette/SVG) als PNG @ 2x scale
 *   3. Upload zu Cloudinary unter `lampenwerk24/funnel-previews/<funnel-type>/<YYYY-MM>/`
 *   4. Returned secure_url - der Funnel speichert sie als `_Vorschau-URL` Cart-Property
 *
 * Verwendung in einem Funnel-JS:
 *   window.LWFunnelPreview.captureAndUpload(previewEl, {
 *     funnelType: 'ortstafel-eingang',
 *     cloudinary: DATA.cloudinary,
 *     backgroundColor: '#f5f5f5',
 *   }).then(function(url) {
 *     state.set('preview_url', url);
 *   });
 *
 * Bei Cart-Submit:
 *   props['_Vorschau-URL'] = state.load().preview_url;
 *
 * In Shopify Order-View erscheint die URL klickbar - oeffnet das exakte Bild
 * was der Kunde im Konfigurator gesehen hat. Production kann 1:1 daran arbeiten.
 */

(function () {
  'use strict';

  if (window.LWFunnelPreview) return; // schon initialisiert

  const HTML2CANVAS_URL = 'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js';
  let html2canvasPromise = null;

  function loadHtml2Canvas() {
    if (window.html2canvas) return Promise.resolve(window.html2canvas);
    if (html2canvasPromise) return html2canvasPromise;

    html2canvasPromise = new Promise(function (resolve, reject) {
      const script = document.createElement('script');
      script.src = HTML2CANVAS_URL;
      script.async = true;
      script.onload = function () {
        if (window.html2canvas) resolve(window.html2canvas);
        else reject(new Error('html2canvas nicht verfuegbar nach Load'));
      };
      script.onerror = function () {
        reject(new Error('html2canvas konnte nicht geladen werden'));
      };
      document.head.appendChild(script);
    });

    return html2canvasPromise;
  }

  /**
   * Prueft ob ein Element + alle Vorfahren tatsaechlich gerendert sind.
   * (display:none auf Vorfahren wuerde html2canvas-Capture sabotieren).
   */
  function isVisible(el) {
    let cur = el;
    while (cur && cur !== document.body) {
      const style = window.getComputedStyle(cur);
      if (style.display === 'none' || style.visibility === 'hidden') return false;
      cur = cur.parentElement;
    }
    return true;
  }

  /**
   * Capture-Element direkt (sichtbar im Viewport).
   */
  function captureDirect(element, html2canvas, scale, backgroundColor) {
    return html2canvas(element, {
      scale: scale,
      backgroundColor: backgroundColor,
      logging: false,
      useCORS: true,
      allowTaint: false,
    });
  }

  /**
   * Capture via Off-Screen-Clone.
   * Wenn der Funnel-Step versteckt ist (display:none auf Eltern-Container),
   * clonen wir das Preview-Element in einen sichtbaren off-screen Container.
   * Computed-Styles werden vom Browser uebernommen weil das Clone im DOM bleibt.
   */
  function captureViaClone(element, html2canvas, scale, backgroundColor) {
    return new Promise(function (resolve, reject) {
      const width = element.offsetWidth || element.getBoundingClientRect().width || 600;
      const height = element.offsetHeight || element.getBoundingClientRect().height || 400;

      const offscreen = document.createElement('div');
      offscreen.setAttribute('aria-hidden', 'true');
      offscreen.style.position = 'fixed';
      offscreen.style.top = '0';
      offscreen.style.left = '-99999px';
      offscreen.style.width = width + 'px';
      offscreen.style.height = height + 'px';
      offscreen.style.opacity = '0';
      offscreen.style.pointerEvents = 'none';
      offscreen.style.zIndex = '-1';
      offscreen.style.background = backgroundColor;

      const clone = element.cloneNode(true);
      // Sicherstellen dass das Clone selbst auch sichtbar ist (Eltern hatten ggf. display:none)
      clone.style.display = '';
      clone.style.visibility = 'visible';
      offscreen.appendChild(clone);
      document.body.appendChild(offscreen);

      // 2 Frames warten - Layout + Font-Render
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          html2canvas(clone, {
            scale: scale,
            backgroundColor: backgroundColor,
            logging: false,
            useCORS: true,
            allowTaint: false,
            width: width,
            height: height,
          }).then(function (canvas) {
            if (offscreen.parentNode) offscreen.parentNode.removeChild(offscreen);
            resolve(canvas);
          }).catch(function (err) {
            if (offscreen.parentNode) offscreen.parentNode.removeChild(offscreen);
            reject(err);
          });
        });
      });
    });
  }

  /**
   * Capture ein DOM-Element als Data-URL (PNG).
   * Funktioniert auch wenn das Element in einem versteckten Step liegt.
   */
  function capture(element, opts) {
    if (!element) return Promise.reject(new Error('Capture: kein Element'));
    opts = opts || {};
    const scale = opts.scale || 2;
    const backgroundColor = opts.backgroundColor || '#f5f5f5';

    return loadHtml2Canvas().then(function (html2canvas) {
      const rect = element.getBoundingClientRect();
      const visible = rect.width > 0 && rect.height > 0 && isVisible(element);
      const captureFn = visible ? captureDirect : captureViaClone;
      return captureFn(element, html2canvas, scale, backgroundColor);
    }).then(function (canvas) {
      return canvas.toDataURL('image/png', 0.92);
    });
  }

  /**
   * Upload eine Data-URL zu Cloudinary.
   * Returns Promise<secure_url>.
   */
  function uploadDataURL(dataURL, opts) {
    opts = opts || {};
    const cloud = opts.cloudinary || {};
    if (!cloud.cloud_name || !cloud.upload_preset) {
      return Promise.reject(new Error('Cloudinary-Config fehlt (cloud_name / upload_preset)'));
    }
    const funnelType = opts.funnelType || 'unknown';
    const folder = opts.folder || ('lampenwerk24/funnel-previews/' + funnelType + '/' + new Date().toISOString().slice(0, 7));

    return new Promise(function (resolve, reject) {
      const formData = new FormData();
      formData.append('file', dataURL);
      formData.append('upload_preset', cloud.upload_preset);
      formData.append('folder', folder);

      const xhr = new XMLHttpRequest();
      xhr.open('POST', 'https://api.cloudinary.com/v1_1/' + cloud.cloud_name + '/image/upload');
      xhr.onload = function () {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const res = JSON.parse(xhr.responseText);
            if (res && res.secure_url) resolve(res.secure_url);
            else reject(new Error('Cloudinary-Response ohne secure_url'));
          } catch (e) {
            reject(new Error('Cloudinary-Response nicht parsbar'));
          }
        } else {
          reject(new Error('Cloudinary-Upload HTTP ' + xhr.status));
        }
      };
      xhr.onerror = function () {
        reject(new Error('Cloudinary-Upload Network-Error'));
      };
      xhr.send(formData);
    });
  }

  /**
   * Combined: Capture + Upload. Returns Promise<cloudinaryURL>.
   * Im Fehlerfall wird der Promise rejected - Funnel sollte das fangen
   * und ohne Vorschau-URL fortfahren (nicht den Cart-Submit blocken).
   */
  function captureAndUpload(element, opts) {
    return capture(element, opts).then(function (dataURL) {
      return uploadDataURL(dataURL, opts);
    });
  }

  /**
   * Schild mit den Eingaben des Kunden fuer die Bestellung (02.11.2026).
   * Max: "ich will da das echte sehen, also mit den Eingaben". Das Blatt fuer Opa
   * (Rechnung + Lieferschein) zeigte bei Ortsschild, HU, Kennzeichen & Co. nur das
   * allgemeine Produktbild. Jetzt wird das Schild im Hintergrund abfotografiert,
   * sobald die Eingabe kurz ruht, und als `_Schild-Vorschau` mitgegeben.
   *
   * Im Hintergrund, nicht beim Klick: so kommt der Link auch beim Sofort-Kaufen-Knopf
   * mit (der liest nur die Formularfelder), und "In den Warenkorb" wartet hoechstens
   * kurz auf ein Bild, das gerade entsteht. Scheitert etwas, geht die Bestellung ohne
   * Bild durch; sie wird nie blockiert.
   *
   * cfg = { sig(): String, element(): HTMLElement, opts: {funnelType, cloudinary, ...}, aktiv?(): Boolean }
   */
  function schildAutomatik(cfg) {
    var RUHE_MS = 900, TAKT_MS = 500;
    var zuletzt = null, seit = 0, fertig = {}, kaputt = {}, laeuft = null;
    function sig() { try { return (cfg.aktiv && !cfg.aktiv()) ? '' : String(cfg.sig() || ''); } catch (e) { return ''; } }
    function starten(s) {
      var el;
      try { el = cfg.element(); } catch (e) { return Promise.resolve(''); }
      var p = captureAndUpload(el, cfg.opts).then(function (url) {
        if (url) fertig[s] = url; else kaputt[s] = true;
        return url || '';
      }, function () { kaputt[s] = true; return ''; });
      laeuft = { sig: s, p: p };
      p.then(function () { if (laeuft && laeuft.sig === s) laeuft = null; });
      return p;
    }
    setInterval(function () {
      var s = sig();
      if (!s) return;
      if (s !== zuletzt) { zuletzt = s; seit = Date.now(); return; }
      if (fertig[s] || kaputt[s] || laeuft || Date.now() - seit < RUHE_MS) return;
      starten(s);
    }, TAKT_MS);
    return {
      url: function () { return fertig[sig()] || ''; },
      warte: function (maxMs) {
        var s = sig();
        if (!s) return Promise.resolve('');
        if (fertig[s]) return Promise.resolve(fertig[s]);
        var p = (laeuft && laeuft.sig === s) ? laeuft.p : (kaputt[s] ? Promise.resolve('') : starten(s));
        return Promise.race([p, new Promise(function (r) { setTimeout(function () { r(''); }, maxMs || 3500); })]);
      }
    };
  }

  window.LWFunnelPreview = {
    capture: capture,
    uploadDataURL: uploadDataURL,
    captureAndUpload: captureAndUpload,
    loadHtml2Canvas: loadHtml2Canvas,
    schildAutomatik: schildAutomatik,
  };
})();